import { Fragment } from 'react';
import {
  ActionableNotification,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { useAtomValue } from 'jotai';
import classNames from 'classnames';
import moment from 'moment';
import { OutputSection } from 'components/output-section/output-section';
import { US_DATE_FORMAT } from 'utils/dates';
import { formatNumber } from 'utils/number';
import type { Pricing } from 'types/evaluation';
import type { AggregatedFreeCashFlowResultResponse } from 'types/api-responses';
import {
  activeFilterCountAtom,
  displayAggregationAtom,
  mapScopeAtom,
  serverFiltersAtom,
} from './atoms';
import { useEval3Page } from './context';
import { useCalcDetail, useCalcLifecycle, useEvalResult } from './hooks';
import { EMPTY } from './utils';
import styles from './package-overview.module.scss';
import {
  SLOT_CATEGORY_LABELS,
  shownPricingSlotCategories,
  type SlotCategoryKey,
} from 'utils/slot-categories';

/**
 * The three pricing sections, in render order. `mtmPricing` is optional on the
 * response — its section renders only when present (see `PackageOverview`). `title`
 * heads the section; `totalLabel` labels the highlighted total row.
 */
const PRICING_SECTIONS = [
  { key: 'targetPricing', title: 'Target Pricing', totalLabel: 'Target Price' },
  { key: 'maxPricing', title: 'Max Pricing', totalLabel: 'Max Price' },
  { key: 'mtmPricing', title: 'MTM Pricing', totalLabel: 'MTM Price' },
] as const;

/**
 * One row per shown slot category plus a highlighted total. `type` matches `Pricing.type`
 * (the row is looked up by it); the total row's label comes from the section.
 *
 * @param {SlotCategoryKey[]} shown - The categories to give a row (shownPricingSlotCategories).
 * @return {Array} The row configs.
 */
const pricingRows = (
  shown: readonly SlotCategoryKey[]
): { type: Pricing['type']; label: string }[] => [
  ...shown.map((type) => ({
    type,
    label: SLOT_CATEGORY_LABELS[type],
  })),
  { type: 'total', label: '' },
];

/**
 * The recalced pricing rollup shape — derived from the `eval_result` response (single
 * source of truth) so the prop can't drift from the BE contract.
 */
type PackageOverviewData = NonNullable<
  AggregatedFreeCashFlowResultResponse['results'][number]['result']
>['packageOverview'];

export interface PackageOverviewProps {
  /** The recalced pricing rollup (Broker = `excluded*`, SIM = `included*`). Absent → empty-indicator cells. */
  packageOverview?: PackageOverviewData;
  evalDate: string | null;
  transactionEffectiveDate: string | null;
  discountToDate: string | null;
  evalExpiration: string | null;
}

/**
 * Formats an ISO date to the US display format, or the em-dash when absent.
 *
 * @param {string | null} date - The ISO date string, or null.
 * @return {string} The formatted date, or `EMPTY`.
 */
const formatDate = (date: string | null): string => {
  return date ? moment(date).format(US_DATE_FORMAT) : EMPTY;
};

/**
 * Formats a dollar value (2-decimal currency with commas), em-dash when absent.
 *
 * @param {number | undefined} value - The dollar amount.
 * @return {string} The formatted currency, or `EMPTY`.
 */
const formatCurrency = (value: number | undefined): string =>
  formatNumber(value, 2, 2, 'currency', true, EMPTY);

/**
 * Formats a plain decimal (2 places, no grouping), em-dash when absent.
 *
 * @param {number | undefined} value - The number.
 * @return {string} The formatted decimal, or `EMPTY`.
 */
const formatDecimal = (value: number | undefined): string =>
  formatNumber(value, 2, 2, 'decimal', false, EMPTY);

/**
 * Formats an implied discount rate as a percentage, em-dash when absent (0 renders
 * as `0.00%`, not `EMPTY`).
 *
 * @param {number | undefined} value - The rate (already a percentage value).
 * @return {string} The formatted rate with a `%` suffix, or `EMPTY`.
 */
const formatRate = (value: number | undefined): string => {
  return value === undefined ? EMPTY : `${formatDecimal(value)}%`;
};

/**
 * Presentational Package Overview — the recalced pricing rollup. Renders a meta-date
 * header (Eval Date / Transaction Effective Date / Discount to Date / Eval
 * Expiration) over per-section pricing tables (Target / Max / optional MTM), each
 * with Broker (Excluding Commissions → `excluded*`) and SIM (Including Commissions →
 * `included*`) columns and a highlighted total row. Renders the FULL shell even with
 * no data (empty-indicator cells) — the table is the section's permanent fixture.
 * Pure: data in, no atoms / fetching / context (the connected wrapper handles those).
 * Structure mirrors the
 * legacy `aggregated-free-cash-flow/package-overview.tsx` (the same `Pricing` shape),
 * re-authored here eval3-styled — no cross-`/pages` import.
 *
 * @param {PackageOverviewProps} props - The rollup (optional) + the four meta dates.
 * @return {JSX.Element} The date header + pricing tables.
 */
export const PackageOverview = ({
  packageOverview,
  evalDate,
  transactionEffectiveDate,
  discountToDate,
  evalExpiration,
}: PackageOverviewProps) => {
  const dateFields = [
    { label: 'Eval Date', value: evalDate },
    { label: 'Transaction Effective Date', value: transactionEffectiveDate },
    { label: 'Discount to Date', value: discountToDate },
    { label: 'Eval Expiration', value: evalExpiration },
  ];

  // MTM is optional — render its section only when the response carries rows, so an
  // absent MTM doesn't show a section of em-dashes.
  const sections = PRICING_SECTIONS.filter(
    (section) =>
      section.key !== 'mtmPricing' ||
      (packageOverview?.mtmPricing?.length ?? 0) > 0
  );
  // A Create New Well status gets a row only when the run priced it (INS-13303).
  const shownCategories = shownPricingSlotCategories(
    PRICING_SECTIONS.map((section) => packageOverview?.[section.key])
  );

  return (
    <div className={styles.packageOverview}>
      <dl className={styles.dateHeader}>
        {dateFields.map(({ label, value }) => (
          <div key={label} className={styles.dateRow}>
            <dt className={styles.dateLabel}>{label}</dt>
            <dd className={styles.dateValue}>{formatDate(value)}</dd>
          </div>
        ))}
      </dl>

      <TableContainer>
        <Table size="sm" className={styles.table}>
          <TableBody>
            {sections.map((section) => {
              const rows = packageOverview?.[section.key];
              return (
                <Fragment key={section.key}>
                  <TableRow>
                    <TableCell colSpan={8} className={styles.sectionTitleCell}>
                      {section.title}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} />
                    <TableCell
                      colSpan={3}
                      className={classNames(
                        styles.groupCell,
                        styles.brokerCell
                      )}
                    >
                      Excluding Commissions (Broker Pricing)
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      className={classNames(styles.groupCell, styles.simCell)}
                    >
                      Including Commissions (SIM Valuation)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableHeader
                      scope="col"
                      className={classNames(
                        styles.headerCell,
                        styles.typeColumn
                      )}
                    >
                      Type
                    </TableHeader>
                    <TableHeader scope="col" className={styles.headerCell}>
                      NRA
                    </TableHeader>
                    <TableHeader
                      scope="col"
                      className={classNames(
                        styles.headerCell,
                        styles.brokerCell
                      )}
                    >
                      Implied Discount Rate
                    </TableHeader>
                    <TableHeader
                      scope="col"
                      className={classNames(
                        styles.headerCell,
                        styles.brokerCell
                      )}
                    >
                      $/NRA
                    </TableHeader>
                    <TableHeader
                      scope="col"
                      className={classNames(
                        styles.headerCell,
                        styles.brokerCell
                      )}
                    >
                      Total $ Amount
                    </TableHeader>
                    <TableHeader
                      scope="col"
                      className={classNames(styles.headerCell, styles.simCell)}
                    >
                      Implied Discount Rate
                    </TableHeader>
                    <TableHeader
                      scope="col"
                      className={classNames(styles.headerCell, styles.simCell)}
                    >
                      $/NRA
                    </TableHeader>
                    <TableHeader
                      scope="col"
                      className={classNames(styles.headerCell, styles.simCell)}
                    >
                      Total $ Amount
                    </TableHeader>
                  </TableRow>
                  {pricingRows(shownCategories).map((rowConfig) => {
                    const row = rows?.find(
                      (pricing) => pricing.type === rowConfig.type
                    );
                    const isTotal = rowConfig.type === 'total';
                    const cellClass = isTotal ? styles.totalCell : undefined;
                    return (
                      <TableRow key={`${section.key}-${rowConfig.type}`}>
                        <TableCell className={cellClass}>
                          {isTotal ? section.totalLabel : rowConfig.label}
                        </TableCell>
                        <TableCell className={cellClass}>
                          {formatDecimal(row?.nra)}
                        </TableCell>
                        <TableCell className={cellClass}>
                          {formatRate(row?.excludedDiscountRate)}
                        </TableCell>
                        <TableCell className={cellClass}>
                          {formatCurrency(row?.excludedDollarsPerNra)}
                        </TableCell>
                        <TableCell className={cellClass}>
                          {formatCurrency(row?.excludedNpv)}
                        </TableCell>
                        <TableCell className={cellClass}>
                          {formatRate(row?.includedDiscountRate)}
                        </TableCell>
                        <TableCell className={cellClass}>
                          {formatCurrency(row?.includedDollarsPerNra)}
                        </TableCell>
                        <TableCell className={cellClass}>
                          {formatCurrency(row?.includedNpv)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

/**
 * Connected Package Overview section — reads page identity + the viewed eval's
 * calc-detail (for `status`) + the dataset filters / spatial scope / display
 * aggregation, fetches `useEvalResult`, and drives the presentational
 * `PackageOverview` through its states. Mirrors the connected `ExecutiveSummary`.
 * Gated by the Layout Settings `packageOverview` toggle at the mount site (T7).
 *
 * States — the table is the PERMANENT fixture (package mode / a draft render it as
 * the empty-indicator shell); everything else is chrome layered above it:
 *  - **Loading** — any cause (`isFetching || useCalcLifecycle.isRunning`): the table
 *    DIMS in place + the title spinner (`aria-busy` for a11y).
 *  - **Error** — a notification + Retry ABOVE the table (never replaces it), shown
 *    only when NOT loading (a run/refetch in flight supersedes a stale error).
 *  - **Loaded** — the rollup (empty-indicator cells for a settled calc with no blob),
 *    with a "Filtered view" banner when a filter/scope is active.
 *
 * @return {JSX.Element} The Package Overview section.
 */
export function PackageOverviewSection() {
  const { calculationId } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const aggregationKey = useAtomValue(displayAggregationAtom);
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const activeFilterCount = useAtomValue(activeFilterCountAtom);

  const status = calcDetail?.status;
  const { data, isFetching, error, refetch } = useEvalResult({
    calcId: calculationId,
    status,
    aggregationKey,
    filters,
    mapScope,
  });

  // No calc (workspace / draft): blank explicitly — `keepPreviousData` would otherwise
  // hold the previously-viewed calc's numbers even though the query is disabled.
  const overview =
    calculationId === null
      ? undefined
      : data?.results[0]?.result?.packageOverview;
  // The unified loading umbrella: this section's OWN eval_result fetch OR the shared
  // calc-lifecycle wait (detail resolving / BE processing) — one loading language for
  // both causes. At completion the two flags hand off within the same render (the
  // status flip closes `isRunning` and optimistically opens `isFetching`), so the
  // user sees one continuous loading from run start to rollup.
  const { isRunning } = useCalcLifecycle(calculationId);
  const isLoading = isFetching || isRunning;
  // Meta-date header, matching the legacy eval-3 `aggregated-free-cash-flow` source:
  // Eval Date = the calc's `createdAt`; Transaction Effective Date = the effective date;
  // Discount to Date = the anchor the run was PRICED at (INS-11484); Eval Expiration
  // = createdAt + 14d.
  const evalDate = calcDetail?.createdAt ?? null;
  const transactionEffectiveDate =
    calcDetail?.configurations?.effective_date ?? null;
  // Server-resolved (`EvaluationCalculation#resolved_discount_to_date`) — the same
  // answer the calculator priced with, read off the run's own record rather than
  // recomputed live, so re-opening an old eval reproduces the anchor it actually
  // used even if the reusable case has since been edited.
  //
  // Deliberately NOT `configurations.evaluation_case_overrides.discount_to_date`:
  // the stored override is only the first rung of override → case → effective date,
  // so reading it alone showed the effective date for every run whose evaluation
  // case supplies the date without a per-run override — the read-side defect
  // INS-11837 fixed in Ruby, which this header would otherwise have repeated in TS.
  // `null` (a legacy run, or one that anchored on its effective date) falls back to
  // the effective date, which is what the calculator used.
  const discountToDate =
    calcDetail?.resolvedDiscountToDate ?? transactionEffectiveDate;
  // Date-only (`YYYY-MM-DD`) so the view's `moment().format()` re-parse has no time/tz
  // to shift — keeps Expiration exactly 14 days after the (locally-displayed) Eval Date.
  // (`.toISOString()` would force UTC and could land a day off vs the local Eval Date.)
  const evalExpiration = calcDetail?.createdAt
    ? moment(calcDetail.createdAt).add(14, 'days').format('YYYY-MM-DD')
    : null;

  return (
    <OutputSection
      title="Package Overview"
      isLoading={isLoading}
      defaultExpanded
    >
      {activeFilterCount > 0 && (
        <InlineNotification
          className={styles.banner}
          kind="info"
          lowContrast
          hideCloseButton
          title="Filtered view"
          subtitle="Showing results based on the filters applied in the left panel."
        />
      )}
      {/* Error is a NOTIFICATION over the fixture, never a replacement — the kept
              (or empty-shell) table stays; Retry lives INSIDE the notification
              (ActionableNotification — InlineNotification can't host actions). Suppressed
              while loading: a run/refetch in flight supersedes a stale error (RQ holds
              `error` until the next success, including across a re-run's disabled window). */}
      {error && !isLoading && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the results"
          subtitle={error.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}
      {/* Permanent fixture — dims in place while loading; aria-busy carries the
              a11y signal. */}
      <div
        className={classNames({ [styles.dimmed]: isLoading })}
        aria-busy={isLoading}
      >
        <PackageOverview
          packageOverview={overview}
          evalDate={evalDate}
          transactionEffectiveDate={transactionEffectiveDate}
          discountToDate={discountToDate}
          evalExpiration={evalExpiration}
        />
      </div>
    </OutputSection>
  );
}
